import { useMemo } from 'react';
import CloseIcon from '../../assets/icons/close.svg?react';
import { readMachineStatus } from 'tf-pulse-shared';
import { useMachineDetailTabs } from '../../contexts/machineDetailTabs';
import { IS_DEMO } from '../../demo/demoMode';
import { MachineStatusBadge } from '../../pages/machines/MachineStatusBadge';
import { getMachineStatusTone } from '../../pages/machines/machineStatusTone';
import { APP_BAR_TONES } from '../appBarTones';
import {
  Machine as GeneratedMachine,
  useGetMachineQuery,
} from '../../gql/generated';
import { machineDataTabs } from '../../pages/machines/machineDataTabs';
import { MACHINE_OWN_TABS } from '../../pages/machines/machineOwnTabs';
import { cn } from '../../utils/className';
import {
  SidePanelTab,
  SidePanelTabContent,
  SidePanelTabList,
  SidePanelTabs,
} from '../SidePanel';
import { machineSubtitle } from './machineSubtitle';

type Machine = GeneratedMachine;

interface MachineDetailPaneProps {
  machine: Machine;
  /** Draws the pane's own header. The side panel draws its own instead. */
  showHeader?: boolean;
  onClose?: () => void;
}

/**
 * The machine detail body: its data tabs around Details and Events. Renders
 * inside the side panel dialog, or inline beside the list on a wide tablet.
 */
export function MachineDetailPane({
  machine: listMachine,
  showHeader = false,
  onClose,
}: MachineDetailPaneProps) {
  // The list row carries no available signals, and the data tabs need them.
  const { data } = useGetMachineQuery({ variables: { id: listMachine.id } });
  const machine = data?.machine ?? listMachine;
  const subtitle = machineSubtitle(machine);
  const status = readMachineStatus(machine.state).status;
  const replacementTabs = useMachineDetailTabs();
  const dataTabs = useMemo(() => machineDataTabs(machine), [machine]);
  const tabs = replacementTabs ?? [
    ...dataTabs.leading,
    ...MACHINE_OWN_TABS,
    ...dataTabs.trailing,
  ];
  const tabOrder = tabs.map((tab) => tab.value);

  return (
    <>
      {showHeader && (
        <div
          className={cn(
            `bg-brand-primary border-border-primary flex items-start
            justify-between gap-4 border-b px-4 py-3`,
            IS_DEMO &&
              cn('bg-linear-68', APP_BAR_TONES[getMachineStatusTone(status)])
          )}
        >
          <div className='min-w-0 flex-1'>
            <h2 className='size-h4 text-inverted truncate font-bold'>
              {machine.displayName || machine.machineId}
            </h2>
            {subtitle && (
              <p className='size-p2 text-inverted/80 mt-0.5 truncate'>
                {subtitle}
              </p>
            )}
          </div>

          {/* The demo shows the status here, as in its side panel. */}
          {IS_DEMO && <MachineStatusBadge status={status} />}

          <button
            type='button'
            className='text-inverted/80 hover:text-inverted
              focus-visible:ring-inverted shrink-0 cursor-pointer rounded-sm p-1
              transition-colors focus-visible:ring-2 focus-visible:outline-none'
            aria-label='Close panel'
            onClick={onClose}
          >
            <CloseIcon className='h-5 w-5' />
          </button>
        </div>
      )}

      <SidePanelTabs
        // The data tabs arrive with the full machine; open on the first one.
        key={tabOrder.join()}
        defaultValue={tabOrder[0]}
        className={cn(
          'flex h-full min-h-0 flex-col',
          showHeader && '[--info-gutter:1rem]'
        )}
        enableSwipe={!showHeader}
        tabOrder={tabOrder}
      >
        <SidePanelTabList>
          {tabs.map((tab) => (
            <SidePanelTab key={tab.value} value={tab.value}>
              {tab.label}
            </SidePanelTab>
          ))}
        </SidePanelTabList>

        {tabs.map((tab) => (
          <SidePanelTabContent
            key={tab.value}
            value={tab.value}
            className='bg-surface-focus-support flex-1 overflow-y-auto'
          >
            {tab.render(machine)}
          </SidePanelTabContent>
        ))}
      </SidePanelTabs>
    </>
  );
}
